import { useState } from "react";
import { useAtomValue } from "jotai";
import { Button, ErrorState, Spinner } from "@common/index";
import { ComponentColor } from "@common/types";
import { ROUTES } from "@routing/routes";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { currentUserAtom } from "@/store/userAtoms";
import { hasNoLocationMembershipAtom } from "@/store/shipToAtoms";
import { NoLocationNote } from "@customer/_components/NoLocationNote";
import { OrderCard } from "./_components/OrderCard";
import { UploadCertificateModal } from "./_components/UploadCertificateModal";
import { useMeOrders } from "./_helpers/useMeOrders";

const PURCHASE_HISTORY_SECTION_ID = "purchase-history";

// Theme quirk: --color-base-content is #fff on the light theme, so chips on a
// light surface need an explicit readable text token (text-neutral).
const CHIP_CLASSES =
    "btn btn-sm rounded-full bg-base-200 border-base-300 text-neutral font-semibold normal-case";

// The dashboard shows a short preview of recent orders; the full paginated
// history lives at /c/account/orders (reachable via "Manage Orders").
const DASHBOARD_ORDER_PREVIEW_COUNT = 5;

export const AccountDashboard = () => {
    const appUser = useAtomValue(currentUserAtom);
    const hasNoLocationMembership = useAtomValue(hasNoLocationMembershipAtom);
    const { sortedOrders, isLoading, hasError, retry } = useMeOrders();
    const { showSuccess } = useNotification();
    const previewOrders = sortedOrders.slice(0, DASHBOARD_ORDER_PREVIEW_COUNT);

    const [isUploadOpen, setIsUploadOpen] = useState(false);

    // The modal closes on success; the toast is what carries the confirmation.
    const handleUploaded = (locationName: string) => {
        setIsUploadOpen(false);
        showSuccess({
            message: `Resale certificate uploaded for ${locationName}. Our team will review it.`,
        });
    };

    return (
        <div className="bg-base-100 text-neutral min-h-[60vh]">
            <div className="max-w-3xl mx-auto px-4 py-6 md:py-8 flex flex-col gap-6">
                <div className="flex items-center justify-between gap-4">
                    <h1 className="text-3xl font-bold text-neutral">
                        {appUser?.firstName
                            ? `Hi, ${appUser.firstName}`
                            : "Hi there"}
                    </h1>
                    <Button
                        to={ROUTES.BROWSE}
                        color={ComponentColor.Warning}
                        className="text-neutral font-bold normal-case"
                    >
                        Shop Now
                    </Button>
                </div>

                {/* #417: seated on no location. Approved and paid up — the
                    store is just near-empty for them until they are assigned
                    one, and this is where they find out why. Only
                    status 7 says it; the walled statuses never reach here. */}
                {hasNoLocationMembership && <NoLocationNote />}

                {/* Action chips only. Navigation between the account sections
                    is the tab strip in AccountLayout (#162). */}
                <div className="flex flex-wrap gap-2">
                    {/* TODO(backend): CIAM profile/password management flow —
                        no edit-login surface exists yet, so the chip is an
                        honest disabled placeholder. */}
                    <button
                        type="button"
                        id="account-update-login-btn"
                        aria-disabled="true"
                        title="Coming soon"
                        className={`${CHIP_CLASSES} opacity-50 cursor-not-allowed no-animation`}
                    >
                        Update Log In
                    </button>
                    {/* E12-S5 (#240): certificates are per-Location (Q14); the
                        modal asks which one when there is more than one. */}
                    <button
                        type="button"
                        id="account-upload-cert-btn"
                        className={CHIP_CLASSES}
                        onClick={() => setIsUploadOpen(true)}
                    >
                        Upload Resale Certificate
                    </button>
                </div>

                <section
                    id={PURCHASE_HISTORY_SECTION_ID}
                    aria-labelledby="purchase-history-heading"
                >
                    <h2
                        id="purchase-history-heading"
                        className="text-xl font-bold text-neutral mb-3"
                    >
                        Purchase History
                    </h2>

                    {isLoading && (
                        <div className="flex justify-center items-center py-12">
                            <Spinner size="lg" />
                        </div>
                    )}

                    {!isLoading && hasError && <ErrorState onRetry={retry} />}

                    {!isLoading && !hasError && sortedOrders.length === 0 && (
                        <div className="flex flex-col items-center gap-4 py-12 text-center">
                            <p className="text-neutral/70">
                                No orders yet. When you place your first order,
                                it will show up here.
                            </p>
                            <Button
                                to={ROUTES.BROWSE}
                                color={ComponentColor.Warning}
                                className="text-neutral font-bold normal-case"
                            >
                                Shop Now
                            </Button>
                        </div>
                    )}

                    {!isLoading && !hasError && previewOrders.length > 0 && (
                        <div className="flex flex-col gap-4">
                            {previewOrders.map((order) => (
                                <OrderCard key={order.id} order={order} />
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {isUploadOpen && (
                <UploadCertificateModal
                    onUploaded={handleUploaded}
                    onClose={() => setIsUploadOpen(false)}
                />
            )}
        </div>
    );
};
